import { NavigationContainerRefWithCurrent } from '@react-navigation/native'
import { t } from 'app/i18l'
import { navigationRef } from 'app/linking'
import { useServer } from 'app/server/context'
import { getPage } from 'app/server/device_token'
import { AppParamList } from 'app/types'
import { HomeStackParamList } from 'home/stack/types'
import { AuthScreenProps } from 'onboarding/stack/types'
import React, { useCallback, useEffect, useState } from 'react'
import { useScheduleContext } from 'schedule/schedule_context'
import { StatusBoundary } from 'shared/components/status_boundary'

export function AuthScreen({ route }: AuthScreenProps) {
  const code = route.params?.code
  const { server } = useServer()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error>()
  const { setSchedule } = useScheduleContext()

  const navigate = useCallback(
    (page?: string) => {
      if (page === 'my_shifts') {
        setSchedule({ calendar: 'helper' })
      } else if (page === 'next_shifts') {
        setSchedule({ calendar: 'helper', openShiftsOnly: true })
      }

      navigateToPage(navigationRef, page)
    },
    [setSchedule]
  )

  const login = useCallback(async () => {
    setLoading(true)
    if (code) {
      try {
        await server.authenticateViaAuthCode(code)
        const page = await getPage()
        navigate(page)
      } catch (err) {
        console.error(err)
        setError(err)
      } finally {
        setLoading(false)
      }
    } else {
      setError(new Error(t('invalidAuthCode')))
      setLoading(false)
    }
  }, [code, navigate, server])

  useEffect(() => {
    login().catch(() => null)
  }, [login])

  return <StatusBoundary isLoading={loading} error={error}></StatusBoundary>
}

export function getTargetRoute(page?: string): keyof HomeStackParamList {
  switch (page) {
    case 'my_shifts':
    case 'next_shifts':
      return 'schedule'
    case 'messages':
      return 'conversations'
    default:
      return 'home'
  }
}

export function navigateToPage(
  navigationContainerRef: NavigationContainerRefWithCurrent<AppParamList>,
  page?: string
) {
  const targetRoute = getTargetRoute(page)

  if (navigationContainerRef.current?.isReady()) {
    navigationContainerRef.current?.reset({
      index: targetRoute === 'home' ? 0 : 1,
      routes:
        targetRoute === 'home'
          ? [{ name: 'home' }]
          : [{ name: 'home' }, { name: targetRoute }],
    })
  }
}
